<template>
    <ConfirmationModal
        additional-class-name="RemoveExceptionProcedure"
        title="Are you sure?"
        yes="Confirm"
        no="Cancel"
        :progress="loading"
        @input="confirm"
    >
        Please confirm you want to remove the exception.
        <div class="RemoveExceptionProcedure__noteField">
            <label class="RemoveExceptionProcedure__noteLabel">Note</label>
            <textarea
                v-model="note"
                class="RemoveExceptionProcedure__noteInput"
                maxlength="250"
                placeholder="Explain why this exception is being removed"
                rows="3"
            />
            <span class="RemoveExceptionProcedure__charCount">{{ note.length }}/250</span>
            <p
                data-test-id="RemoveExceptionProcedure__helpText"
                class="RemoveExceptionProcedure__helpText"
            >
                Notes should be informative and cannot be edited after saving.
            </p>
        </div>
    </ConfirmationModal>
</template>

<style lang="scss">
    .RemoveExceptionProcedure {
        &__noteField {
            margin-top: 16px;
        }

        &__noteLabel {
            display: block;
            margin-bottom: 6px;
            font-weight: 600;
            color: var(--eidSecureBlue);
        }

        &__noteInput {
            width: 100%;
            padding: 10px 12px;
            border: 1px solid var(--eidSecureBlue-25);
            border-radius: 4px;
            resize: vertical;
            font-size: var(--text-body-size, 14px);
            color: var(--eidSecureBlue);
            background-color: var(--eidWhite);
            box-sizing: border-box;

            &:focus {
                outline: none;
                border-color: var(--eidTrustGreen);
            }

            &::placeholder {
                color: var(--eidTechGray-50);
            }
        }

        &__charCount {
            display: block;
            text-align: right;
            margin-top: 4px;
            font-size: var(--text-small-size, 12px);
            color: var(--eidTechGray);
        }

        &__helpText {
            margin-top: 6px;
            font-size: var(--text-small-size, 12px);
            color: var(--eidTechGray);
        }
    }
</style>

<script lang="ts">
    import { Component, Prop, Vue } from '@evidentid/vue-property-decorator';

    import { Entity } from '@evidentid/tprm-portal-lib/models/dashboard';
    import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

    import ConfirmationModal from '@/components/confirmation-modal/ConfirmationModal.vue';
    import { useEntityDetailsStore } from '@/modules/entity-details/store';

    // PRODUCT-26110 QA fixes4: declares every event this procedure component `$emit`s --
    // without a declared `emits`, Vue 3 warns "Component emitted event 'abort'/'finish'
    // but it is neither declared in the emits option nor as an 'onAbort'/'onFinish' prop"
    // every time it fires.
    @Component({
        components: { ConfirmationModal },
        emits: [ 'abort', 'finish' ],
    })
    export default class RemoveExceptionProcedure extends Vue {
        // PRODUCT-26110 QA fixes4: `uuid` is unused; declared purely so Vue 3 doesn't warn
        // "Extraneous non-props attributes (uuid)" (see UploadEntityDocumentProcedure.vue).
        @Prop({ type: String, default: '' })
        private uuid?: string;

        @Prop({ type: String, default: '' })
        private rpName!: string;

        @Prop({ type: Object })
        private entity!: Entity;

        @Prop({ type: Array, default: () => [] })
        private exceptionIds!: string[];

        private note: string = '';

        private get loading(): boolean {
            return useEntityDetailsStore().removeExceptionStatus[this.rpName] === OperationStatus.loading;
        }

        private async confirm(confirmed: boolean): Promise<void> {
            if (confirmed) {
                await useEntityDetailsStore().removeException({
                    rpName: this.rpName,
                    entityId: this.entity.id,
                    exceptionIds: this.exceptionIds,
                    note: this.note || undefined,
                });
                if (useEntityDetailsStore().removeExceptionStatus[this.rpName] !== OperationStatus.success) {
                    this.$emit('abort');
                    return;
                }
                this.$emit('finish', this.entity.displayName);
            } else {
                this.$emit('abort');
            }
        }

        private close(): void {
            this.$emit('abort');
        }
    }
</script>
